"use client";

import { Clipboard, Check } from "lucide-react";
import React, { useState } from "react";

import { cn } from "@/src/lib/shadcn-utils";

export const COPIED_RESET_DELAY_MS = 1500;

interface CopyableContentProps {
  text: string;
  upperCase?: boolean;
  textClassName?: string;
  iconOnly?: boolean;
  alwaysShow?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export function CopyableContent({
  text,
  upperCase,
  textClassName,
  alwaysShow,
  className,
  children,
}: CopyableContentProps) {
  const [justCopied, setJustCopied] = useState(false);

  const formattedText = upperCase ? text.toUpperCase() : text;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(formattedText);

      setJustCopied(true);

      setTimeout(() => setJustCopied(false), COPIED_RESET_DELAY_MS);
    } catch {}
  }

  return (
    <span
      // Pads each line fragment to the 20px line box
      className={cn("relative group cursor-default py-0.5 text-foreground text-sm", className)}
      onClick={handleCopy}
    >
      <span
        className={`group-hover:opacity-70 transition-opacity duration-200 group-active:opacity-40 ${textClassName}`}
      >
        {children ?? formattedText}
      </span>
      {justCopied ? (
        <Check size={14} className="ml-1 text-green-600 transition-colors group-hover:opacity-100 inline" />
      ) : (
        <Clipboard
          size={14}
          className={`ml-1 text-muted-foreground ${alwaysShow ? "opacity-50" : "opacity-0"} group-hover:opacity-100 transition-opacity inline relative top-[-1px]`}
        />
      )}
    </span>
  );
}

export function CopyIcon({ text }: { text: string }) {
  const [justCopied, setJustCopied] = useState(false);

  const formattedText = text;

  async function handleCopy() {
    if (!formattedText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(formattedText);

      setJustCopied(true);

      setTimeout(() => setJustCopied(false), COPIED_RESET_DELAY_MS);
    } catch {}
  }

  return (
    <span className="relative group cursor-default" onClick={handleCopy}>
      {justCopied ? (
        <Check size={14} className="ml-1 text-green-600 transition-colors group-hover:opacity-100 inline" />
      ) : (
        <Clipboard
          size={14}
          className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity inline relative top-[-1px]"
        />
      )}
    </span>
  );
}
